import type { GetLeadAttributesType } from '@app/queries/lead/leadAttributes';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { type FormBaseType } from '@repo/utils';

export interface LeadFormsFilters {
  searchString?: string;
}

export type LeadAttributeType = GetLeadAttributesType['leadAttributes'][0];

export interface LeadFormsState {
  filters: LeadFormsFilters;
  leadFormEditor: {
    isOpen: boolean;
    form?: FormBaseType;
    activeDraggedAttribute?: LeadAttributeType | null;
    isDraggingAttribute?: boolean;
    usedAttributeIds: string[];
  };
}

const initialState: LeadFormsState = {
  filters: {
    searchString: '',
  },
  leadFormEditor: {
    isOpen: false,
    form: undefined,
    activeDraggedAttribute: null,
    isDraggingAttribute: false,
    usedAttributeIds: [],
  },
};

export const leadFormsSlice = createSlice({
  name: 'leadForms',
  initialState,
  reducers: {
    setLeadFormFilter: (state, action: PayloadAction<LeadFormsFilters>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetLeadFormFilters: (state) => {
      state.filters = initialState.filters;
    },
    setLeadFormEditor: (state, action: PayloadAction<Partial<LeadFormsState['leadFormEditor']>>) => {
      state.leadFormEditor = {
        ...state.leadFormEditor,
        ...action.payload,
      };
    },
    closeLeadFormEditor: (state) => {
      state.leadFormEditor = {
        ...initialState.leadFormEditor,
        isOpen: false,
        form: undefined,
        activeDraggedAttribute: null,
      };
    },
    setActiveDraggedAttribute: (state, action: PayloadAction<LeadAttributeType>) => {
      state.leadFormEditor.activeDraggedAttribute = action.payload;
      state.leadFormEditor.isDraggingAttribute = true;
    },
    clearActiveDraggedAttribute: (state) => {
      state.leadFormEditor.activeDraggedAttribute = null;
      state.leadFormEditor.isDraggingAttribute = false;
    },
    pushUsedAttributeIds: (state, action: PayloadAction<string[]>) => {
      const usedAttributeIdsSet = new Set(state.leadFormEditor.usedAttributeIds);
      action.payload.forEach((id) => usedAttributeIdsSet.add(id));
      state.leadFormEditor.usedAttributeIds = Array.from(usedAttributeIdsSet);
    },
    removeUsedAttributeIds: (state, action: PayloadAction<string[]>) => {
      const usedAttributeIdsSet = new Set(state.leadFormEditor.usedAttributeIds);
      action.payload.forEach((id) => usedAttributeIdsSet.delete(id));
      state.leadFormEditor.usedAttributeIds = Array.from(usedAttributeIdsSet);
    },
  },
});

export const {
  setLeadFormFilter,
  resetLeadFormFilters,
  setLeadFormEditor,
  closeLeadFormEditor,
  setActiveDraggedAttribute,
  clearActiveDraggedAttribute,
  pushUsedAttributeIds,
  removeUsedAttributeIds,
} = leadFormsSlice.actions;

export default leadFormsSlice.reducer;
